import { useHtmlId } from '@ipfabric/frontend-shared-assets/utils/react/HtmlId';
import { ScrollView } from '@ipfabric/ui-components/ScrollView/ScrollView';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type {
  EdgeCenterLabel,
  EdgeLinecapLabel,
  GroupEdgeSettings,
  LinePattern,
  PathLookupEdgeType,
  ProtocolEdgeType,
  TopologyGraphPreset,
} from '@ipfabric/utils';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { DropdownIndicator } from 'components/DropdownIndicator/DropdownIndicator';
import { FormikCheckbox } from 'components/FormikCheckbox/FormikCheckbox';
import { FormikColorField } from 'components/FormikColorField/FormikColorField';
import { FormikErrorMessage } from 'components/FormikErrorMessage/FormikErrorMessage';
import { FormikMultiSelectField } from 'components/FormikMultiSelectField/FormikMultiSelectField';
import { FormikSelectFieldLegacy } from 'components/FormikSelectFieldLegacy/FormikSelectFieldLegacy';
import {
  formikSelectFieldRoundedControlStyle,
  formikSelectFieldRoundedMenuStyle,
} from 'components/FormikSelectFieldLegacy/styles';
import { FormikTextFieldLegacy } from 'components/FormikTextFieldLegacy/FormikTextFieldLegacy';
import type { PresetDraftState } from 'components/Graph/GraphState';
import type {
  EdgeLabel,
  EnabledProtocolLabels,
} from 'components/Graph/helpers/getProtocolLabels';
import { SlidePaneClose } from 'components/SlidePane/SlidePaneClose';
import { useLocale } from 'containers/App/LocaleContext';
import { useFormik } from 'formik';
import type { Location } from 'history';
import { memo } from 'react';
import type { FC } from 'react';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { LineThicknessField } from './LineThickinessField';
import { StrokeOption } from './StrokeOption';
import { StrokeValue } from './StrokeValue';
import {
  getCreateGroupInitialValues,
  getProtocolSettingsInitialValues,
} from './utils/getProtocolSettingsInitialValues';
import { validateProtocolSettings } from './utils/validateProtocolSettings';

export interface SaveSettingsCommand {
  values: FormSettingsValues;
}

interface Props {
  groupId: GroupEdgeSettings['id'] | null;
  labels?: EnabledProtocolLabels;
  onClose: () => void;
  onSave: (command: SaveSettingsCommand, historyAttributes?: Location) => void;
  preset?: TopologyGraphPreset;
  presetDraftState: PresetDraftState;
  protocolName?: ProtocolEdgeType | PathLookupEdgeType;
  protocolTitle?: string;
}

export interface FormSettingsValues {
  color: string;
  // Group has 'label' & 'name' but edge doesn't. Field for 'name' is displayed only settings is for group.
  // So there will not be field with 'undefined' value.
  label?: string;
  labelCenter: EdgeCenterLabel[];
  labelLinecap: EdgeLinecapLabel[];
  wrapCenterLabel: boolean;
  wrapLinecapLabel: boolean;
  name?: string;
  pattern: LinePattern;
  lineThicknessMedium: number;
  lineThicknessThick: number;
  lineThicknessThin: number;
}

const BareGraphProtocolsSettings: FC<Props> = (props) => {
  const msg = useLocale();
  const edgeIsGroup = !props.protocolName;

  const handleSubmit = useStableCallback((values: FormSettingsValues) => {
    props.onSave({ values });
    props.onClose();
  });

  const handleClose = useStableCallback(() => {
    props.onClose();
  });

  const formik = useFormik<FormSettingsValues>({
    enableReinitialize: false,
    initialTouched: {
      lineThicknessThin: true,
      lineThicknessMedium: true,
      lineThicknessThick: true,
    },
    initialValues:
      props.preset && props.labels
        ? getProtocolSettingsInitialValues(
            props.preset,
            props.groupId,
            props.protocolName,
            props.labels,
          )
        : getCreateGroupInitialValues(),
    onSubmit: makeFormikOnSubmit(handleSubmit),
    validate: validateProtocolSettings(msg, edgeIsGroup),
  });

  const strokeOptions = Object.keys(
    msg.canvasGraph.graphSidePanel.layoutSettings.protocolSettingsWindow.form
      .strokeOptions,
  ).map((value) => ({
    value,

    label:
      msg.canvasGraph.graphSidePanel.layoutSettings.protocolSettingsWindow.form
        .strokeOptions[value as LinePattern],
  }));

  const labelCenterOptions = Object.keys(props.labels?.center ?? {}).map(
    (value) => ({
      value,
      label:
        msg.canvasGraph.graphSidePanel.layoutSettings.protocolSettingsWindow
          .form.labels[value as EdgeCenterLabel],
    }),
  );

  const labelLinecapOptions = Object.keys(props.labels?.linecap ?? {}).map(
    (value) => ({
      value,

      label:
        msg.canvasGraph.graphSidePanel.layoutSettings.protocolSettingsWindow
          .form.labels[value as EdgeLinecapLabel],
    }),
  );

  const edgeLabels = props.labels
    ? (Object.keys({
        ...props.labels.center,
        ...props.labels.linecap,
      }) as EdgeLabel[])
    : [];

  const groupNameId = useHtmlId('groupName');
  const edgeLabelId = useHtmlId('edgeLabel');
  const patternId = useHtmlId('pattern');

  return (
    <div className='flex flex-col match-parent'>
      <SlidePaneClose
        className='graph-layout-settings-protocol-pane__close'
        onClose={handleClose}
        label={
          msg.canvasGraph.graphSidePanel.layoutSettings
            .backToProtocolsVisualizationSetup
        }
      />
      <ScrollView className='flex-1' contentClassName='py-8'>
        <p className='ipf-form-label px-5 pb-2.5 font-bold'>
          {edgeIsGroup
            ? msg.canvasGraph.graphSidePanel.layoutSettings
                .protocolSettingsWindow.form.protocolGroupSettings
            : msg.canvasGraph.graphSidePanel.layoutSettings.protocolSettingsWindow.form.protocolSettings(
                props.protocolTitle ?? '',
              )}
        </p>
        {edgeIsGroup && (
          <>
            <div className='flex'>
              <div className='flex-1 pl-5 pr-2.5'>
                <label className='ipf-form-label' htmlFor={groupNameId}>
                  {
                    msg.canvasGraph.graphSidePanel.layoutSettings
                      .protocolSettingsWindow.form.groupName
                  }
                </label>
                <FormikTextFieldLegacy
                  id={groupNameId}
                  className='ipf-input w-full'
                  errorClassName='block'
                  formik={formik}
                  name='name'
                />
              </div>
              <div className='flex-1 pl-2.5 pr-5'>
                <label className='ipf-form-label' htmlFor={edgeLabelId}>
                  {
                    msg.canvasGraph.graphSidePanel.layoutSettings
                      .protocolSettingsWindow.form.edgeLabel
                  }
                </label>
                <FormikTextFieldLegacy
                  id={edgeLabelId}
                  className='ipf-input w-full'
                  errorClassName='block'
                  formik={formik}
                  name='label'
                />
              </div>
            </div>
            <hr className='graph-layout-settings-protocol-divider mx-5' />
          </>
        )}
        <div className='flex pb-5'>
          <div className='flex-1 pl-5 pr-2.5'>
            <label className='ipf-form-label' htmlFor={undefined}>
              {
                msg.canvasGraph.graphSidePanel.layoutSettings
                  .protocolSettingsWindow.form.color
              }
            </label>
            <FormikColorField
              formik={formik}
              name='color'
              wrapperClassName='block'
            />
            <FormikErrorMessage formik={formik} name='color' />
          </div>
          <div className='flex-1 pl-2.5 pr-5'>
            <label className='ipf-form-label' htmlFor={patternId}>
              {
                msg.canvasGraph.graphSidePanel.layoutSettings
                  .protocolSettingsWindow.form.pattern
              }
            </label>
            <FormikSelectFieldLegacy
              id={patternId}
              formik={formik}
              name='pattern'
              isSearchable={false}
              isClearable={false}
              isDisabled={formik.isSubmitting}
              field={formik.getFieldProps('pattern')}
              components={{
                DropdownIndicator,
                Option: StrokeOption as any,
                SingleValue: StrokeValue as any,
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '34px',
                  width: '100%',
                  ...formikSelectFieldRoundedControlStyle,
                }),
                menu: (base) => ({
                  ...base,
                  ...formikSelectFieldRoundedMenuStyle,
                }),
              }}
              options={strokeOptions}
            />
            <FormikErrorMessage formik={formik} name='pattern' />
          </div>
        </div>
        <div className='px-5'>
          <p className='ipf-form-label font-bold'>
            {
              msg.canvasGraph.graphSidePanel.layoutSettings
                .protocolSettingsWindow.form.lineWidth
            }
            <Tooltip
              content={
                msg.canvasGraph.graphSidePanel.layoutSettings
                  .protocolSettingsWindow.form.lineWidthTooltip
              }
            >
              <i className='fa fa-fw fa-question-circle ml-1' />
            </Tooltip>
          </p>
          <LineThicknessField
            formik={formik}
            name='lineThicknessThin'
            thickness='thin'
          />
          <LineThicknessField
            formik={formik}
            name='lineThicknessMedium'
            thickness='medium'
          />
          <LineThicknessField
            formik={formik}
            name='lineThicknessThick'
            thickness='thick'
          />
          <div className='flex h-10 flex-row items-center'>
            <span
              className={classNames(
                'graph-layout-settings-protocol-options__thickness',
                'graph-layout-settings-protocol-options__thickness-more',
              )}
              style={{
                borderBottomColor: formik.values.color,
                borderBottomStyle: formik.values.pattern,
              }}
            />
            <span className='ipf-form-label mb-0 shrink-0 whitespace-nowrap pl-5'>
              {msg.canvasGraph.graphSidePanel.layoutSettings.protocolSettingsWindow.form.moreThanXEdges(
                formik.values.lineThicknessThick,
              )}
            </span>
          </div>
        </div>

        <hr className='graph-layout-settings-protocol-divider mx-5' />
        <div className='flex flex-col gap px-5'>
          <div>
            <label className='ipf-form-label' htmlFor={undefined}>
              {
                msg.canvasGraph.graphSidePanel.layoutSettings
                  .protocolSettingsWindow.form.centerLabel
              }
            </label>
            {edgeLabels.length > 0 && (
              <FormikMultiSelectField
                name='labelCenter'
                formik={formik}
                searchable
                options={labelCenterOptions}
                noBottomSpacing
                className='mb-xs'
              />
            )}
            <FormikCheckbox
              formik={formik}
              name='wrapCenterLabel'
              description='Show each value on a new line'
            />
          </div>

          <div>
            <label className='ipf-form-label' htmlFor={undefined}>
              {
                msg.canvasGraph.graphSidePanel.layoutSettings
                  .protocolSettingsWindow.form.linecapLabel
              }
            </label>
            {edgeLabels.length > 0 && (
              <FormikMultiSelectField
                name='labelLinecap'
                formik={formik}
                searchable
                options={labelLinecapOptions}
                noBottomSpacing
                className='mb-xs'
              />
            )}
            <FormikCheckbox
              formik={formik}
              name='wrapLinecapLabel'
              description='Show each value on a new line'
            />
          </div>
        </div>
      </ScrollView>
      <hr className='graph-layout-settings-protocol-divider mx-5' />
      <div className='flex justify-between px-5 mb-5'>
        <Button
          variant='secondary'
          className='mr-2'
          type='button'
          onClick={handleClose}
          disabled={props.presetDraftState !== 'idle'}
        >
          {
            msg.canvasGraph.graphSidePanel.layoutSettings.protocolSettingsWindow
              .btnClose
          }
        </Button>
        <Button
          variant='primary'
          type='button'
          onClick={() => {
            formik.handleSubmit();
          }}
          disabled={props.presetDraftState !== 'idle'}
        >
          {
            msg.canvasGraph.graphSidePanel.layoutSettings.protocolSettingsWindow
              .btnSave
          }
        </Button>
      </div>
    </div>
  );
};

export const GraphProtocolsSettings = memo(BareGraphProtocolsSettings);
